/**
 * 音声チューニングナレッジ 1 件のカード。
 *
 * ## 状態バッジを見出しの隣に置く理由
 *
 * 読む人が最初に判断したいのは **「使える手があるのか」**。
 * 本文を読み終えてから「結局まだ直っていない」と分かるのは時間の無駄なので、
 * 状態 (`✅ 解決済み` / `🟡 改善方法あり` / `⚪ 未解決`) を見出しの隣に出す。
 *
 * ⚠️ 記号だけ・色だけでは状態を伝えない。記号 + 文言を必ず併記する
 *   (色の判別が難しい利用者、記号が環境で表示されない場合に備える)。
 *
 * ## 未解決を「空のカード」に見せない
 *
 * `unsolved` は改善方法 (`workaround`) と SSML を持たない。
 * 項目を空欄で並べると「書きかけ」に見えてしまうため、
 * **値がある項目だけを描画する**。代わりに「分かっていること」(`cause`) と
 * 「次に確かめること」(`nextAction`) を必ず出し、記録としての価値を示す。
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける** (静的データを import しない)。
 *   Phase2 で API 化しても本コンポーネントは無変更で使える (R14)。
 * - 最終確認日を必ず出す。Polly の Neural エンジンは更新されるため、
 *   いつ時点の記録かが分からないと「今も同じか」を判断できない。
 * - 操作要素は置かない (Readme は読むだけ)。SSML は編集欄へ貼るため
 *   `<pre>` で改行と空白を保持する。
 */
import type React from 'react';

import { findSsmlKnowledgeStatus } from './knowledgeStatus';
import type { SsmlTuningKnowledge } from './types';
import { ssmlUseCaseTagLabel } from './useCaseTags';

export interface KnowledgeCardProps {
  /** 表示するナレッジ */
  knowledge: SsmlTuningKnowledge;
}

export function KnowledgeCard({ knowledge }: KnowledgeCardProps): React.JSX.Element {
  /*
   * 未知の状態キー (データ追加時のタイプミス) でも描画を落とさない。
   * 記号は空、文言はキーそのままにして「定義漏れ」が見えるようにする。
   */
  const statusDefinition = findSsmlKnowledgeStatus(knowledge.status);

  return (
    <li
      className={`ssml-knowledge-card is-${knowledge.status}`}
      data-testid={`ssml-knowledge-${knowledge.id}`}
      id={`knowledge-${knowledge.id}`}
    >
      <div className="ssml-knowledge-card__head">
        <p className="ssml-knowledge-card__title">{knowledge.title}</p>
        {/* 記号 + 文言。色に依存せず状態が読めるようにする */}
        <span
          className={`ssml-knowledge-card__status is-${knowledge.status}`}
          data-testid={`ssml-knowledge-status-${knowledge.id}`}
          title={statusDefinition?.description}
        >
          {statusDefinition
            ? `${statusDefinition.icon} ${statusDefinition.label}`
            : knowledge.status}
        </span>
        {/* いつ時点の記録かを必ず示す (合成結果は更新で変わりうる) */}
        <span
          className="ssml-knowledge-card__verified"
          data-testid={`ssml-knowledge-verified-${knowledge.id}`}
        >
          最終確認 {knowledge.verifiedAt}
        </span>
      </div>

      <ul className="ssml-usecase-card__tags">
        {knowledge.tags.map((tag) => (
          <li key={tag} className="ssml-usecase-card__tag">
            {ssmlUseCaseTagLabel(tag)}
          </li>
        ))}
      </ul>

      {/*
        値がある項目だけを出す。空欄を並べると「書きかけ」に見え、
        未解決の記録が「価値の無いカード」と受け取られてしまう。
      */}
      <dl className="ssml-knowledge-card__fields">
        <dt>課題</dt>
        <dd>{knowledge.problem}</dd>

        {knowledge.cause ? (
          <>
            <dt>分かっていること</dt>
            <dd>{knowledge.cause}</dd>
          </>
        ) : null}

        {knowledge.workaround ? (
          <>
            <dt>今できる対処</dt>
            <dd>{knowledge.workaround}</dd>
          </>
        ) : null}

        {knowledge.ssml ? (
          <>
            <dt>SSML</dt>
            <dd>
              <pre className="ssml-usecase-card__ssml">{knowledge.ssml}</pre>
            </dd>
          </>
        ) : null}

        <dt>次に確かめること</dt>
        <dd>{knowledge.nextAction}</dd>
      </dl>
    </li>
  );
}
